/**
 * The setup the agent proposed, while the person is looking at it.
 *
 * A store and not screen state for the reason `link-draft-store.ts` gives: the
 * proposal outlives more than one screen. The talk screen receives it, the
 * proposal screen draws it, and one record's detail screen is pushed over
 * that. A `useState` on the talk screen would be gone by the third.
 *
 * Nothing here has been written anywhere. The proposal screen's Create button
 * is the one write, through `POST /v1/onboarding/compose`, and until that tap
 * this is words on a phone. One proposal at a time: a new conversation clears
 * it, and a new proposal replaces it.
 *
 * Leaving setup keeps what was said (B13 of docs/plans/setup-coach-build.md).
 * The kept thread — the messages, the understanding, the proposals — and the
 * language persist through `AsyncStorage` for seven days, stamped with the
 * signed-in user they belong to, with Create's key for each setup it was
 * tapped on (`createKeyFor`). `resume` drops a kept thread that is older
 * than that or someone else's; Create (`parkCreated`) and sign-out (`forget`,
 * called from `forgetDevice`) clear it. Create keeps the language, with its
 * stamp, so it too goes after seven days. Only those fields persist: the held
 * setup, the landing's parked id and the parked repair or change are this
 * session's hand-offs between screens, not the conversation.
 */
import type { AgentLanguageTag, AssistantLanded, ComposedSetup, SetupUnderstanding } from '@alluzo/shared';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { UIMessage } from 'ai';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { newLocalId } from '@/lib/local-id';

/** How long a kept setup thread waits for the person to come back: seven days. */
export const SETUP_DRAFT_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

/** The AsyncStorage key the kept thread is written under. */
export const SETUP_DRAFT_STORAGE_KEY = 'alluzo.setup-draft';

/**
 * The business's first memory, posted beside the setup on Create (decisions
 * 1, 3 and 13 of docs/plans/agent-business-memory.md): what the interview
 * noted, the turns it was said in, and whether they were typed or spoken.
 * Held with the proposal because it is the proposal's: a new conversation
 * clears both together.
 */
export type SetupMemory = {
  readonly understanding: SetupUnderstanding | null;
  readonly transcript: readonly UIMessage[];
  readonly source: 'interview' | 'call';
};

const NO_MEMORY: SetupMemory = { understanding: null, transcript: [], source: 'interview' };

/** What leaving setup keeps, and whose it is. */
export type KeptSetupThread = {
  /** The signed-in user the thread belongs to. Null until the first `keep`. */
  readonly owner: string | null;
  /** When it was last kept, in epoch ms. Null when nothing is kept. */
  readonly savedAt: number | null;
  readonly messages: readonly UIMessage[];
  readonly understanding: SetupUnderstanding | null;
  /** Every setup Coach built in this thread, oldest first. */
  readonly proposals: readonly ComposedSetup[];
  /**
   * Create's `clientKey` for each setup it was tapped on, by the setup's JSON.
   * One key per setup, whichever place creates it and however often it is
   * mounted, so a create whose answer was lost replays rather than makes a
   * second business. Kept with the thread: a retry after a relaunch replays too.
   */
  readonly createKeys: Readonly<Record<string, string>>;
  /**
   * The id the setup's conversation is kept under (step 5.2): Create posts it
   * beside the interview, the server stores the interview as the new
   * business's first thread under it, and live mode carries on into that
   * thread after Create. Null until the conversation first asks for it.
   */
  readonly threadId: string | null;
};

const NOTHING_KEPT: KeptSetupThread = { owner: null, savedAt: null, messages: [], understanding: null, proposals: [], createKeys: {}, threadId: null };

/** A kept thread has something in it: the Close asks "Leave setup?" only then. */
export const hasKept = (thread: Pick<KeptSetupThread, 'messages' | 'understanding' | 'proposals'>): boolean =>
  thread.messages.length > 0 || thread.understanding !== null || thread.proposals.length > 0;

/** A kept thread stamped `savedAt` is stale at `now` once seven days have passed. */
export const isStale = (savedAt: number | null, now: number): boolean =>
  savedAt !== null && now - savedAt > SETUP_DRAFT_KEEP_MS;

/**
 * AsyncStorage, with a refusal swallowed: a disk that will not read or write
 * leaves the thread in memory for this session, never a rejected promise.
 */
const keptOnDisk = {
  getItem: (key: string) => AsyncStorage.getItem(key).catch(() => null),
  setItem: (key: string, value: string) => AsyncStorage.setItem(key, value).catch(() => undefined),
  removeItem: (key: string) => AsyncStorage.removeItem(key).catch(() => undefined),
};

type SetupDraftState = KeptSetupThread & {
  /**
   * Keep the thread for `owner`, stamped now. Only the parts given change.
   */
  keep: (owner: string, thread: Partial<Pick<KeptSetupThread, 'messages' | 'understanding' | 'proposals'>>) => void;
  /**
   * Setup opens for `owner`. A kept thread that is someone else's or older
   * than seven days is dropped, with its language. True when a thread is left
   * to resume.
   */
  resume: (owner: string, now?: number) => boolean;
  /** Create's key for `setup`: the one it already has, or a new one kept for it. */
  createKeyFor: (setup: ComposedSetup) => string;
  /** The conversation's thread id: the one it has, or a new one kept for it. */
  threadIdFor: () => string;
  /** Sign-out: nothing of the last person's setup stays, in memory or on disk. */
  forget: () => void;
  /**
   * The language the person chose for the agent on the picker before the call.
   * Null until they choose: the picker then offers the phone's own language.
   * Kept with the thread and not cleared with the proposal, so a second
   * business opens the picker with the same row already on.
   */
  language: AgentLanguageTag | null;
  setLanguage: (language: AgentLanguageTag) => void;
  /** Landed inside the new business: nothing is parked any more, the landing's words included. */
  landed: () => void;
  setup: ComposedSetup | null;
  /** What the agent said follows once the setup is created, drawn under Create. */
  next: string | null;
  /** What the person should hear beside the proposal: a card that could not be drafted, a check that did not run. */
  notes: readonly string[];
  /** What Create posts beside the setup, so the business remembers its own interview. */
  memory: SetupMemory;
  /** The agent proposed this. The proposal screen draws it next. */
  hold: (setup: ComposedSetup, next?: string | null, notes?: readonly string[], memory?: SetupMemory) => void;
  /**
   * The business Create just made. Parked so `SetupLanding` in the root layout,
   * which survives the `/v1/me` reload that unmounts every onboarding screen,
   * can switch to it and land on its dashboard.
   */
  createdId: string | null;
  /**
   * What the landing's call opens on (B12): the business's name, its first
   * record's entry word and its first list, read from the created setup
   * before this store lets that setup go, and parked beside `createdId` for
   * the same reason. Null when nothing was given.
   */
  landing: AssistantLanded | null;
  /**
   * The thread Create kept the interview under, parked beside `createdId`:
   * the landing opens the call on it, so live mode carries on in the same
   * thread (step 5.2). Null when Create kept no interview.
   */
  landingThread: string | null;
  /** The records exist. The landing switches to the business, and the kept thread is done. */
  parkCreated: (tenantId: string, landing?: AssistantLanded | null, threadId?: string | null) => void;
  /**
   * Create is out or has made the business (B11), from the card or the
   * review page: the business is the setup that was sent, so the session
   * builds nothing more on a change said now (B10). A refusal or a failure
   * lets it go. In memory only; a new conversation starts with it off.
   */
  createOut: boolean;
  setCreateOut: (out: boolean) => void;
  /** A new conversation. Does not touch createdId: talk stays mounted under the proposal. */
  clear: () => void;
  /**
   * A compose refusal, parked for the talk screen to hand to the agent. The
   * proposal screen is not the conversation, so it cannot send; it parks the
   * sentence here and pops back, and the talk screen sends it once. The plan
   * gives the agent one round trip to repair what the compose route refused.
   */
  repair: string | null;
  askRepair: (sentence: string) => void;
  clearRepair: () => void;
};

export const useSetupDraftStore = create<SetupDraftState>()(
  persist(
    (set, get) => ({
      ...NOTHING_KEPT,
      keep: (owner, thread) => set({ ...thread, owner, savedAt: Date.now() }),
      resume: (owner, now = Date.now()) => {
        const state = get();
        if (isStale(state.savedAt, now) || (state.owner !== null && state.owner !== owner)) {
          set({ ...NOTHING_KEPT, language: null });
          return false;
        }
        return hasKept(state);
      },
      createKeyFor: (setup) => {
        const held = get().createKeys;
        const of = JSON.stringify(setup);
        const kept = held[of];
        if (kept !== undefined) return kept;
        const key = newLocalId();
        set({ createKeys: { ...held, [of]: key } });
        return key;
      },
      threadIdFor: () => {
        const held = get().threadId;
        if (held !== null) return held;
        const id = newLocalId();
        set({ threadId: id });
        return id;
      },
      forget: () => {
        set({ ...NOTHING_KEPT, language: null, setup: null, next: null, notes: [], memory: NO_MEMORY, createdId: null, landing: null, landingThread: null, repair: null, createOut: false });
        void useSetupDraftStore.persist.clearStorage();
      },
      language: null,
      setLanguage: (language) => set({ language, savedAt: Date.now() }),
      landed: () => set({ createdId: null, landing: null, landingThread: null }),
      setup: null,
      next: null,
      notes: [],
      memory: NO_MEMORY,
      hold: (setup, next = null, notes = [], memory = NO_MEMORY) => set({ setup, next, notes, memory }),
      createdId: null,
      landing: null,
      landingThread: null,
      // The language outlives the thread for the next business, and keeps the
      // thread's stamp, so it still goes when its seven days are up.
      parkCreated: (tenantId, landing = null, threadId = null) => {
        const { language, savedAt } = get();
        set({
          ...NOTHING_KEPT,
          savedAt: language === null ? null : (savedAt ?? Date.now()),
          createdId: tenantId,
          landing,
          landingThread: threadId,
          setup: null,
          memory: NO_MEMORY,
        });
      },
      createOut: false,
      setCreateOut: (createOut) => set({ createOut }),
      // A new conversation is a new thread.
      clear: () => set({ setup: null, next: null, notes: [], memory: NO_MEMORY, createOut: false, threadId: null }),
      repair: null,
      askRepair: (sentence) => set({ repair: sentence }),
      clearRepair: () => set({ repair: null }),
    }),
    {
      name: SETUP_DRAFT_STORAGE_KEY,
      storage: createJSONStorage(() => keptOnDisk),
      partialize: (state) => ({
        owner: state.owner,
        savedAt: state.savedAt,
        messages: state.messages,
        understanding: state.understanding,
        proposals: state.proposals,
        createKeys: state.createKeys,
        threadId: state.threadId,
        language: state.language,
      }),
      // A thread past its seven days never reaches memory: it rehydrates as nothing.
      merge: (persisted, current) => {
        const kept = persisted as Partial<KeptSetupThread & { language: AgentLanguageTag | null }> | undefined;
        if (kept === undefined || isStale(kept.savedAt ?? null, Date.now())) return current;
        return { ...current, ...kept };
      },
    },
  ),
);
